import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HOME_EXAMPLES } from "@/lib/pathways";
import { LIFE_CATEGORIES, WAYS } from "@/lib/lifeCategories";
import { EmailCapture } from "@/components/EmailCapture";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Home() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [usage, setUsage] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HOME_EXAMPLES.length), 2200);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { api.get("/find-usage").then((r) => setUsage(r.data)).catch(() => {}); }, [user]);

  const go = (e) => {
    e.preventDefault();
    const term = q.trim() || `I need ${HOME_EXAMPLES[idx]}`;
    nav(`/find?q=${encodeURIComponent(term)}`);
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12">
        <div className="eyebrow mb-6 fade-up">FIND THE CHEAPER WAY.</div>
        <h1 className="font-display leading-[0.9] text-5xl sm:text-7xl lg:text-8xl fade-up" style={{ animationDelay: "60ms" }}>
          STOP PAYING FOR SHIT<br />
          <span className="text-[#EF3340]">YOU DON'T HAVE</span><br />
          TO PAY FOR.
        </h1>
        <p className="font-serif text-xl md:text-2xl text-stone-300 max-w-2xl mt-8 leading-snug fade-up" style={{ animationDelay: "140ms" }}>
          Find the cheaper way, the free way, the used way, the deal, the benefit, the better time to
          buy — or discover you shouldn't buy it at all. A consumer intelligence desk for everyday life.
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mt-10 fade-up" style={{ animationDelay: "200ms" }}>
          <Link to="/find" data-testid="start-audit-hero" className="group inline-flex items-center gap-3 font-label text-sm tracking-widest bg-[#EF3340] text-white px-8 py-5 hover:bg-[#cc2a35] transition-colors">
            START A FIND <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/hungry" data-testid="hungry-hero" className="group inline-flex items-center gap-3 font-label text-sm tracking-widest border hairline text-stone-200 px-8 py-5 hover:border-[#EF3340] hover:text-[#EF3340] transition-colors">
            HUNGRY? <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <span className="font-label text-[10px] tracking-widest text-stone-500">{usage?.unlimited ? "UNLIMITED FINDS" : "3 FREE FINDS TODAY · NO CARD REQUIRED"}</span>
        </div>
      </section>

      <section className="border-y hairline bg-[#0e0c12]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl md:text-5xl leading-none mb-8">WHAT ARE YOU ABOUT TO<br />SPEND MONEY ON?</h2>
          <form onSubmit={go} className="border hairline bg-[#141217] flex flex-col md:flex-row">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`"I need ${HOME_EXAMPLES[idx]}"`} data-testid="home-search-input"
              className="flex-1 bg-transparent px-6 py-6 text-xl md:text-2xl font-serif text-stone-100 placeholder:text-stone-600 outline-none" />
            <button type="submit" data-testid="home-search-btn" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-8 py-6 hover:bg-[#cc2a35] transition-colors whitespace-nowrap">FIND THE CHEAPER WAY →</button>
          </form>
          <div className="flex flex-wrap gap-2 mt-4">
            {HOME_EXAMPLES.slice(0, 9).map((ex) => (
              <button key={ex} onClick={() => nav(`/find?q=${encodeURIComponent("I need " + ex)}`)}
                className="font-label text-xs border hairline px-3 py-2 text-stone-400 hover:border-[#EF3340] hover:text-[#EF3340] transition-colors">{ex}</button>
            ))}
          </div>
        </div>
      </section>

      {/* BROWSE BY LIFE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <div className="eyebrow mb-3">BROWSE BY LIFE</div>
            <h2 className="font-display text-4xl md:text-6xl leading-none">WHATEVER YOU'RE<br />ABOUT TO SPEND ON.</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-l border-t hairline">
          {LIFE_CATEGORIES.map((c, i) => (
            <Link key={c.slug} to={`/life/${c.slug}`} data-testid={`home-life-${c.slug}`}
              className="border-r border-b hairline p-6 hover:bg-[#141217] transition-colors group">
              <div className="font-label text-[10px] tracking-widest text-stone-600 mb-2">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-lg leading-tight group-hover:text-[#EF3340] transition-colors">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* WAYS TO GET IT */}
      <section className="border-y hairline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="eyebrow mb-3">WAYS TO GET IT</div>
          <h2 className="font-display text-4xl md:text-6xl leading-none mb-6">YOU DON'T NEED A CHEAPER<br />PRODUCT. YOU NEED A CHEAPER WAY.</h2>
          <div className="flex flex-wrap gap-2 mt-8">
            {WAYS.map((w) => (
              <Link key={w.label} to={w.to} className="font-label text-xs tracking-widest border hairline px-3 py-2 text-stone-300 hover:border-[#EF3340] hover:text-[#EF3340] transition-colors">{w.label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT TYPES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="eyebrow mb-3">START YOUR AUDIT</div>
        <h2 className="font-display text-4xl md:text-6xl leading-none mb-10">HOW DO YOU WANT TO START?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ["ONE PURCHASE", "About to buy something? Run it through the audit first.", "/find"],
            ["MY SUBSCRIPTIONS", "Find overlap and waste across everything you pay monthly.", "/audit?type=subscriptions"],
            ["SECONDHAND SHOPPING", "Before you buy it new, see what already exists.", "/life/secondhand"],
            ["BUY WHEN", "Is now actually the wrong time to buy?", "/buy-when"],
            ["WHAT DO I ALREADY HAVE?", "The Benefit Finder. You might already have access.", "/benefits"],
            ["OPPORTUNITIES", "Live deals, free stuff, cashback, settlements, local finds.", "/opportunities"],
          ].map(([title, desc, to]) => (
            <Link key={title} to={to} data-testid={`audit-type-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group border hairline bg-[#141217] p-7 hover:border-[#EF3340] transition-colors flex flex-col">
              <h3 className="font-display text-2xl mb-2">{title}</h3>
              <p className="font-serif text-sm text-stone-400 leading-relaxed mb-6">{desc}</p>
              <span className="mt-auto font-label text-xs tracking-widest text-[#EF3340] flex items-center gap-2">RUN IT <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      <EmailCapture />
    </div>
  );
}
